import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * When quantity is at minimum (1), shows a delete icon and removes the item on click.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * In cart context, minus button is never disabled — it toggles between
   * minus icon (decrement) and delete icon (remove item) at min quantity.
   */
  updateButtonStates() {
    const { minusButton, plusButton, minusIcon, deleteIcon } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart: minus button is never disabled (shows delete at min instead)
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    // Toggle between minus and delete icons
    if (minusIcon && deleteIcon) {
      const atMin = value <= min;
      minusIcon.classList.toggle('hidden', atMin);
      deleteIcon.classList.toggle('hidden', !atMin);
    }
  }

  /**
   * Handles the quantity decrease event.
   * At minimum quantity, sets to 0 to trigger item removal.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { value, min } = this.getCurrentValues();

    if (value <= min) {
      // At minimum: set to 0 to trigger removal via cart-items-component
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
    } else {
      this.updateQuantity(-1);
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
